import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Divider, Typography } from '@material-ui/core';
import DescriptionIcon from '@mui/icons-material/DescriptionRounded';
import { alpha } from '@mui/material/styles';
import { getTone, toneAlpha, grey } from '../../../../theme/tones';
import {
  APPRENANT_GROUP_LABEL,
  APPRENANT_PATH,
  COMPTE_RENDU_TAB,
  FORMATEUR_TAB,
  QUESTIONNAIRE_TYPES
} from './constants';

/**
 * Construit une query string en ignorant les valeurs vides : le contexte est
 * partiel la plupart du temps (formation sans session, ou rien du tout).
 */
const buildQuery = (parts) => {
  const params = new URLSearchParams();
  Object.entries(parts).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') params.set(key, String(value));
  });
  const query = params.toString();
  return query ? `?${query}` : '';
};

const itemSx = {
  px: 2.25,
  py: 1,
  borderRadius: '12px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  fontWeight: 800,
  fontSize: '0.8rem',
  textDecoration: 'none',
  transition: 'all .2s ease'
};

/**
 * La barre d'onglets des réponses aux questionnaires, partagée par les trois
 * écrans qui l'affichent (apprenant, formateurs, comptes rendus) pour qu'elle
 * ne bouge pas d'un pixel quand on passe de l'un à l'autre.
 *
 * Elle mélange volontairement trois natures d'entrées, d'où le groupement :
 * les trois types apprenant derrière l'étiquette « Apprenant », l'audience
 * formateur, puis le document.
 *
 * `onSelectType` n'existe que sur la page apprenant, seule à pouvoir changer de
 * type sans naviguer ; ailleurs les trois premiers onglets sont des liens.
 * `context` (formation, session, type) suit l'utilisateur d'un écran à l'autre.
 */
const ResponsesTabs = ({ active, onSelectType, context }) => {
  const formationId = context?.formationId ?? null;
  const sessionId = context?.sessionId ?? null;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        gap: 0.5,
        p: 0.5,
        mb: 2.5,
        borderRadius: '16px',
        bgcolor: 'background.paper',
        border: `1px solid ${grey(100)}`,
        boxShadow: (theme) => `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}`,
        maxWidth: '100%',
        overflowX: 'auto',
        alignItems: 'center'
      }}
    >
      {/* Sans cette étiquette, « Satisfaction » se lit comme le pendant de
          « Satisfaction des formateurs » alors que ce sont deux niveaux
          différents : un type d'un côté, une audience de l'autre. */}
      <Typography
        sx={{
          pl: 1.5, pr: 0.75, whiteSpace: 'nowrap',
          fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.06em',
          textTransform: 'uppercase', color: 'text.disabled'
        }}
      >
        {APPRENANT_GROUP_LABEL} :
      </Typography>

      {QUESTIONNAIRE_TYPES.map((t) => {
        const selected = t.value === active;
        const selectedSx = {
          ...itemSx,
          color: selected ? grey(0) : getTone('neutral').base,
          background: selected ? t.gradient : 'transparent',
          boxShadow: selected ? `0 4px 12px ${t.accent}4D` : 'none',
          '&:hover': { bgcolor: selected ? undefined : toneAlpha('neutral', 0.07) }
        };

        return onSelectType ? (
          <Box key={t.value} onClick={() => onSelectType(t.value)} sx={selectedSx}>
            {t.label}
          </Box>
        ) : (
          <Box
            key={t.value}
            component={RouterLink}
            to={`${APPRENANT_PATH}${buildQuery({ type: t.value, formation: formationId })}`}
            sx={selectedSx}
          >
            {t.label}
          </Box>
        );
      })}

      {/* Les formateurs sont une AUDIENCE, pas un 4e type : leurs réponses
          vivent sur une page à part (une session n'a qu'un formateur, il n'y a
          rien à regrouper par session). L'onglet garde malgré tout sa place
          ici — c'est la même question posée par l'utilisateur : « qu'ont
          répondu les gens à nos questionnaires ? ». */}
      <Divider orientation="vertical" flexItem sx={{ mx: 0.75, my: 0.75, borderColor: 'divider' }} />
      {/* La formation suit l'utilisateur ici aussi : côté formateurs elle
          n'ouvre pas un niveau mais pré-remplit le filtre — même résultat à
          l'écran, on ne recommence pas sa recherche en changeant d'onglet. */}
      <Box
        component={active === FORMATEUR_TAB.value ? 'div' : RouterLink}
        to={active === FORMATEUR_TAB.value
          ? undefined
          : `${FORMATEUR_TAB.path}${buildQuery({ formation: formationId })}`}
        sx={{
          ...itemSx,
          color: active === FORMATEUR_TAB.value ? grey(0) : 'text.secondary',
          background: active === FORMATEUR_TAB.value ? FORMATEUR_TAB.gradient : 'transparent',
          boxShadow: active === FORMATEUR_TAB.value ? `0 4px 12px ${FORMATEUR_TAB.accent}4D` : 'none',
          '&:hover': {
            bgcolor: active === FORMATEUR_TAB.value
              ? undefined
              : (theme) => alpha(theme.palette.text.secondary, 0.07)
          }
        }}
      >
        {FORMATEUR_TAB.label}
      </Box>

      {/* Un document, pas un jeu de réponses : second séparateur et style à
          part pour que la barre reste lisible. */}
      <Divider orientation="vertical" flexItem sx={{ mx: 0.75, my: 0.75, borderColor: 'divider' }} />
      <Box
        component={active === COMPTE_RENDU_TAB.value ? 'div' : RouterLink}
        to={active === COMPTE_RENDU_TAB.value
          ? undefined
          : `${COMPTE_RENDU_TAB.path}${buildQuery({ type: context?.type, formation: formationId, session: sessionId })}`}
        sx={{
          ...itemSx,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.6,
          color: active === COMPTE_RENDU_TAB.value ? grey(0) : COMPTE_RENDU_TAB.accent,
          background: active === COMPTE_RENDU_TAB.value ? COMPTE_RENDU_TAB.gradient : 'transparent',
          border: active === COMPTE_RENDU_TAB.value
            ? '1px solid transparent'
            : `1px solid ${toneAlpha('blue', 0.35)}`,
          boxShadow: active === COMPTE_RENDU_TAB.value ? `0 4px 12px ${COMPTE_RENDU_TAB.accent}4D` : 'none',
          '&:hover': {
            bgcolor: active === COMPTE_RENDU_TAB.value ? undefined : COMPTE_RENDU_TAB.accentSoft
          }
        }}
      >
        <DescriptionIcon sx={{ fontSize: 16 }} />
        {COMPTE_RENDU_TAB.label}
      </Box>
    </Box>
  );
};

ResponsesTabs.propTypes = {
  /** Valeur de l'onglet courant : un type apprenant, `Formateurs` ou `ComptesRendus`. */
  active: PropTypes.string.isRequired,
  /** Fourni uniquement par la page apprenant, qui change de type sans naviguer. */
  onSelectType: PropTypes.func,
  /** Contexte transporté d'un écran à l'autre : { type, formationId, sessionId }. */
  context: PropTypes.object
};

export default ResponsesTabs;
